/**
 * One relay channel (packages/shared/src/schemas/relayChannel.ts) for a byte stream: a workbench's terminal
 * (`terminals.subscribe`) or a node's console (`node.shell.subscribe`). One socket per channel, opened with a single-use
 * ticket so no token sits in a URL. Typing (`input: true`) asks for a ticket after a recent sign-in, as the screen's
 * control does; a stale sign-in rejects with the API's `sign_in_again` (ApiError) before anything opens.
 *
 * Frames are read loosely: a scope the shared list does not know yet (a node's `shell:*`) must not drop the `opened`
 * frame. Credit is granted back as the view handles what arrived, a quarter of the window at a time.
 */

import { RELAY_CHANNEL_INITIAL_CREDIT, RELAY_CHANNEL_SUBPROTOCOL, type ChannelCloseCode } from '@aioe/shared';

import { routes } from '../api/client.ts';

export interface ChannelHandlers {
  onOpened(result: unknown, scopes: string[]): void;
  onData(data: unknown): void;
  onClosed(close: { code: ChannelCloseCode; reason?: string }): void;
}

export interface ByteChannel {
  input(payload: unknown): void;
  close(): void;
}

const PING_MS = 30_000;
const STEP = RELAY_CHANNEL_INITIAL_CREDIT / 4;
const utf8 = new TextEncoder();

/** The refusal a machine put at the head of a `closed.reason` (`"shell_off: …"`), if any. */
export function refusalOf(reason: string | undefined): string | undefined {
  return /^([a-z_]+):/.exec(reason ?? '')?.[1];
}

/** Closes that mean "stop trying": the same open would only be refused again. */
export const FINAL_CLOSES: ReadonlySet<ChannelCloseCode> = new Set(['revoked', 'scope', 'refused', 'done', 'client_closed']);

export async function openChannel(deviceId: string, open: { method: string; params: Record<string, unknown>; input: boolean }, h: ChannelHandlers): Promise<ByteChannel> {
  const ticket = await routes.channelTicket(deviceId, open.input ? { stepUp: true } : {});
  const ws = new WebSocket(ticket.url, RELAY_CHANNEL_SUBPROTOCOL);
  let ch: string | undefined;
  let ended = false;
  let handled = 0;
  const send = (f: unknown) => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify(f));
  const end = (code: ChannelCloseCode, reason?: string) => {
    if (ended) return;
    ended = true;
    clearInterval(ping);
    h.onClosed({ code, ...(reason ? { reason } : {}) });
  };
  const ping = setInterval(() => send({ t: 'ping', at: Date.now() }), PING_MS);
  ws.onopen = () => send({ t: 'open', ref: 'stream', rpc: { jsonrpc: '2.0', id: 'stream', method: open.method, params: open.params }, input: open.input });
  ws.onmessage = (ev) => {
    const text = typeof ev.data === 'string' ? ev.data : '';
    let f: { t?: string; ch?: string; scopes?: unknown; result?: unknown; data?: unknown; code?: ChannelCloseCode; reason?: string };
    try {
      f = JSON.parse(text) as typeof f;
    } catch {
      return;
    }
    if (f.t === 'opened' && typeof f.ch === 'string') {
      ch = f.ch;
      h.onOpened(f.result, Array.isArray(f.scopes) ? f.scopes.filter((s): s is string => typeof s === 'string') : []);
    } else if (f.t === 'data' && f.ch === ch) {
      h.onData(f.data);
      handled += utf8.encode(text).length;
      if (handled >= STEP && ch) {
        send({ t: 'credit', ch, bytes: handled });
        handled = 0;
      }
    } else if (f.t === 'closed' || f.t === 'error') {
      end(f.code ?? 'protocol', f.reason);
      if (f.t === 'closed') ws.close(1000);
    }
  };
  ws.onclose = () => end('workbench_offline');
  return {
    input: (payload) => ch && send({ t: 'input', ch, data: payload }),
    close: () => {
      ended = true;
      clearInterval(ping);
      if (ch) send({ t: 'close', ch });
      ws.close(1000);
    },
  };
}
